import { AppleMusic } from "@components/common/icons/apple-music";
import { AppleMusicColor } from "@components/common/icons/apple-music-color";
import { Facebook } from "@components/common/icons/facebook";
import { FacebookColor } from "@components/common/icons/facebook-color";
import { Instagram } from "@components/common/icons/instagram";
import { InstagramColor } from "@components/common/icons/instagram-color";
import { Pinterest } from "@components/common/icons/pinterest";
import { PinterestColor } from "@components/common/icons/pinterest-color";
import { Spotify } from "@components/common/icons/spotify";
import { SpotifyColor } from "@components/common/icons/spotify-color";
import { TikTok } from "@components/common/icons/tik-tok";
import { TikTokColor } from "@components/common/icons/tik-tok-color";
import { Twitch } from "@components/common/icons/twitch";
import { TwitchColor } from "@components/common/icons/twitch-color";
import { Twitter } from "@components/common/icons/twitter";
import { TwitterColor } from "@components/common/icons/twitter-color";
import { YouTube } from "@components/common/icons/you-tube";
import { YouTubeColor } from "@components/common/icons/you-tube-color";
import { ButtonLink } from "@components/common/types/general";
import { GQLChannel } from "graphql.generated";
import { TFunction } from "next-i18next";
import { ReactNode } from "react";

export function resolveChannelIcon(channel: GQLChannel, colorize: boolean = false, className?: string): ReactNode {
    if (channel === GQLChannel.Instagram) {
        return colorize ? <InstagramColor className={className} /> : <Instagram className={className} />;
    }
    if (channel === GQLChannel.Tiktok) {
        return colorize ? <TikTokColor className={className} /> : <TikTok className={className} />;
    }
    if (channel === GQLChannel.Youtube) {
        return colorize ? <YouTubeColor className={className} /> : <YouTube className={className} />;
    }
    if (channel === GQLChannel.Pinterest) {
        return colorize ? <PinterestColor className={className} /> : <Pinterest className={className} />;
    }
    if (channel === GQLChannel.Apple) {
        return colorize ? <AppleMusicColor className={className} /> : <AppleMusic className={className} />;
    }
    if (channel === GQLChannel.Spotify) {
        return colorize ? <SpotifyColor className={className} /> : <Spotify className={className} />;
    }
    if (channel === GQLChannel.Twitch) {
        return colorize ? <TwitchColor className={className} /> : <Twitch className={className} />;
    }
    if (channel === GQLChannel.Twitter) {
        return colorize ? <TwitterColor className={className} /> : <Twitter className={className} />;
    }
    if (channel === GQLChannel.Facebook) {
        return colorize ? <FacebookColor className={className} /> : <Facebook className={className} />;
    }
}

export function resolveChannelName(channel: GQLChannel): string {
    if (channel === GQLChannel.Instagram) {
        return "Instagram";
    }
    if (channel === GQLChannel.Tiktok) {
        return "TikTok";
    }
    if (channel === GQLChannel.Youtube) {
        return "YouTube";
    }
    if (channel === GQLChannel.Pinterest) {
        return "Pinterest";
    }
    if (channel === GQLChannel.Apple) {
        return "Apple";
    }
    if (channel === GQLChannel.Spotify) {
        return "Spotify";
    }
    if (channel === GQLChannel.Twitch) {
        return "Twitch";
    }
    if (channel === GQLChannel.Twitter) {
        return "X (Twitter)";
    }
    if (channel === GQLChannel.Facebook) {
        return "Facebook";
    }
}

export function resolveChannelShortDescription(channel: GQLChannel, t: TFunction): string {
    if (channel === GQLChannel.Instagram) {
        return t("channels.instagram.shortDescription", {
            defaultValue: "Visual content & stories",
        });
    }
    if (channel === GQLChannel.Tiktok) {
        return t("channels.tiktok.shortDescription", {
            defaultValue: "Short-form video content",
        });
    }
    if (channel === GQLChannel.Youtube) {
        return t("channels.youtube.shortDescription", {
            defaultValue: "Long-form video content",
        });
    }
    if (channel === GQLChannel.Pinterest) {
        return t("channels.pinterest.shortDescription", {
            defaultValue: "Visual discovery platform",
        });
    }
    if (channel === GQLChannel.Apple) {
        return t("channels.apple.shortDescription", {
            defaultValue: "Podcasts & audio content",
        });
    }
    if (channel === GQLChannel.Spotify) {
        return t("channels.spotify.shortDescription", {
            defaultValue: "Music & podcast streaming",
        });
    }
    if (channel === GQLChannel.Twitch) {
        return t("channels.twitch.shortDescription", {
            defaultValue: "Live streaming platform",
        });
    }
    if (channel === GQLChannel.Twitter) {
        return t("channels.twitter.shortDescription", {
            defaultValue: "Microblogging platform",
        });
    }
    if (channel === GQLChannel.Facebook) {
        return t("channels.facebook.shortDescription", {
            defaultValue: "Social networking platform",
        });
    }
    return "";
}

export function resolveChannelColor(channel: GQLChannel): string {
    if (channel === GQLChannel.Instagram) {
        return "#405DE6";
    }
    if (channel === GQLChannel.Tiktok) {
        return "#000000";
    }
    if (channel === GQLChannel.Youtube) {
        return "#FF0000";
    }
    if (channel === GQLChannel.Pinterest) {
        return "#E60022";
    }
    if (channel === GQLChannel.Apple) {
        return "#FA233B";
    }
    if (channel === GQLChannel.Spotify) {
        return "#1ED760";
    }
    if (channel === GQLChannel.Twitch) {
        return "#5F4495";
    }
    if (channel === GQLChannel.Twitter) {
        return "#000000";
    }
    if (channel === GQLChannel.Facebook) {
        return "#0079FB";
    }
}

export function resolveChannelLink(channel: GQLChannel, id: string): ButtonLink {
    if (channel === GQLChannel.Instagram) {
        return { href: `https://www.instagram.com/${id}`, target: "_blank" };
    }
    if (channel === GQLChannel.Tiktok) {
        return { href: "#" };
    }
    if (channel === GQLChannel.Youtube) {
        return { href: `/youtube/channel/${id}` };
    }
    if (channel === GQLChannel.Pinterest) {
        return { href: "#" };
    }
    if (channel === GQLChannel.Apple) {
        return { href: "#" };
    }
    if (channel === GQLChannel.Spotify) {
        return { href: "#" };
    }
    if (channel === GQLChannel.Twitch) {
        return { href: "#" };
    }
    if (channel === GQLChannel.Twitter) {
        return { href: `/twitter/profile/${id}` };
    }
    if (channel === GQLChannel.Facebook) {
        return { href: `https://www.facebook.com/${id}`, target: "_blank" };
    }
}

/*
enum Channel {
  facebook
  news
  tmdb
}
*/
